import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  IconButton,
  Tooltip,
  Chip,
} from '@mui/material';
import { Event, Delete, AccessTime, Person, LocationOn } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { Reservation } from './types';
import { formatReservationTime, formatReservationDate } from './formatTime';

interface ReservationInfoDialogProps {
  open: boolean;
  reservation: Reservation | null;
  canDelete: boolean;
  onClose: () => void;
  onDelete: () => void;
  onOpenCalendarMenu: (e: React.MouseEvent<HTMLElement>) => void;
}

const ReservationInfoDialog: React.FC<ReservationInfoDialogProps> = ({
  open,
  reservation,
  canDelete,
  onClose,
  onDelete,
  onOpenCalendarMenu,
}) => {
  const { t } = useTranslation();

  if (!reservation) return null;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{t('stiklainis.reservationDetails')}</DialogTitle>
      <DialogContent>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
          <Typography variant="h6">{reservation.title}</Typography>

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <AccessTime fontSize="small" color="action" />
            <Typography variant="body2">
              {formatReservationDate(reservation.startTime)}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {formatReservationTime(reservation.startTime)} – {formatReservationTime(reservation.endTime)}
            </Typography>
          </Box>

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Person fontSize="small" color="action" />
            <Typography variant="body2">
              {reservation.user.firstName} {reservation.user.lastName}
            </Typography>
          </Box>

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <LocationOn fontSize="small" color="action" />
            <Typography variant="body2">{reservation.room?.name}</Typography>
          </Box>

          {reservation.description && (
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              {reservation.description}
            </Typography>
          )}

          <Chip
            label={
              reservation.status === 'confirmed'
                ? t('stiklainis.statusConfirmed')
                : reservation.status === 'cancelled'
                  ? t('stiklainis.statusCancelled')
                  : t('stiklainis.statusCompleted')
            }
            size="small"
            color={reservation.status === 'confirmed' ? 'success' : reservation.status === 'cancelled' ? 'error' : 'default'}
            sx={{ alignSelf: 'flex-start' }}
          />
        </Box>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Tooltip title={t('calendar.addToCalendar')}>
          <IconButton onClick={onOpenCalendarMenu} size="small">
            <Event />
          </IconButton>
        </Tooltip>
        <Box sx={{ flex: 1 }} />
        <Button onClick={onClose}>{t('common.close', 'Uždaryti')}</Button>
        {canDelete && (
          <Button color="error" startIcon={<Delete />} onClick={onDelete}>
            {t('stiklainis.cancelReservation')}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default ReservationInfoDialog;
